import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { cn } from '@/lib/utils';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import {
  PRODUCT_NAME,
  filterNavItems,
  flattenNavTargets,
  getActiveNavItemId,
  pathMatchesNavItem,
  type NavChild,
  type NavItem,
} from '@/navigation/nav-items';
import { useIsAdmin } from '@/hooks/usePermission';
import { useNavItems } from '@/navigation/use-nav-items';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/SearchInput';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarFooter,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { SidebarTasksButton } from '@/components/tasks/SidebarTasksButton';
import { CountBadge } from '@/components/CountBadge';
import { currentEnvironment, environmentLabel } from '@/lib/app-environment';

function NavLinkItem({
  item,
  isActive,
  isHighlighted,
  onNavigate,
}: {
  item: NavItem;
  isActive: boolean;
  isHighlighted: boolean;
  onNavigate: () => void;
}) {
  const Icon = item.icon;
  const linkRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (isHighlighted) {
      linkRef.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [isHighlighted]);

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={isActive}
        tooltip={item.label}
        className={cn(
          isHighlighted &&
            !isActive &&
            'bg-sidebar-accent text-sidebar-accent-foreground'
        )}
        data-highlighted={isHighlighted || undefined}
      >
        <Link
          ref={linkRef}
          id={`nav-result-${item.id}`}
          data-sidebar-item-key={item.id}
          to={item.path}
          role="option"
          aria-selected={isHighlighted}
          onClick={onNavigate}
        >
          <Icon style={item.iconColour ? { color: item.iconColour } : undefined} />
          <span>{item.label}</span>
          {item.count ? (
            <>
              <CountBadge count={item.count} max={99} className="ml-auto group-data-[collapsible=icon]:hidden" />
              <span className="sr-only">{`: ${item.count} unread`}</span>
            </>
          ) : null}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function NavChildLink({
  child,
  isActive,
  isHighlighted,
  onNavigate,
}: {
  child: NavChild;
  isActive: boolean;
  isHighlighted: boolean;
  onNavigate: () => void;
}) {
  const linkRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (isHighlighted) {
      linkRef.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [isHighlighted]);

  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton
        asChild
        isActive={isActive}
        className={cn(
          isHighlighted &&
            !isActive &&
            'bg-sidebar-accent text-sidebar-accent-foreground'
        )}
        data-highlighted={isHighlighted || undefined}
      >
        <Link
          ref={linkRef}
          id={`nav-result-${child.id}`}
          data-sidebar-item-key={child.id}
          to={child.path}
          role="option"
          aria-selected={isHighlighted}
          onClick={onNavigate}
        >
          <span>{child.label}</span>
        </Link>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  );
}

function NavGroupItem({
  item,
  activeId,
  highlightedId,
  open,
  onOpenChange,
  onNavigate,
  onParentNavigate,
}: {
  item: NavItem;
  activeId: string | null;
  highlightedId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigate: () => void;
  onParentNavigate: () => void;
}) {
  const Icon = item.icon;
  const parentHighlighted = highlightedId === item.id;
  const parentActive = activeId === item.id;
  const linkRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (parentHighlighted) {
      linkRef.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [parentHighlighted]);

  return (
    <Collapsible open={open} onOpenChange={onOpenChange} className="group/collapsible">
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          isActive={parentActive}
          tooltip={item.label}
          className={cn(
            parentHighlighted &&
              !parentActive &&
              'bg-sidebar-accent text-sidebar-accent-foreground'
          )}
          data-highlighted={parentHighlighted || undefined}
        >
          <Link
            ref={linkRef}
            id={`nav-result-${item.id}`}
            data-sidebar-item-key={item.id}
            to={item.path}
            role="option"
            aria-selected={parentHighlighted}
            onClick={onParentNavigate}
          >
            <Icon style={item.iconColour ? { color: item.iconColour } : undefined} />
            <span>{item.label}</span>
          </Link>
        </SidebarMenuButton>
        <CollapsibleTrigger asChild>
          <SidebarMenuAction
            className="data-[state=open]:rotate-90"
            aria-label={`Toggle ${item.label} submenu`}
          >
            <ChevronRight />
          </SidebarMenuAction>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {(item.children ?? []).map((child) => (
              <NavChildLink
                key={child.id}
                child={child}
                isActive={activeId === child.id}
                isHighlighted={highlightedId === child.id}
                onNavigate={onNavigate}
              />
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const navItems = useNavItems();
  const { setOpenMobile, isMobile } = useSidebar();
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setOpenMobile(false);
  }, [location.pathname, setOpenMobile]);

  const availableItems = useMemo(() => filterNavItems(navItems, { isAdmin }), [navItems, isAdmin]);

  const visibleItems = useMemo(
    () => filterNavItems(availableItems, { isAdmin: true, searchQuery }),
    [availableItems, searchQuery]
  );

  /** Parents + nested children for search highlight / Enter. */
  const navTargets = useMemo(
    () => flattenNavTargets(visibleItems),
    [visibleItems]
  );

  // Auto-expand groups whose route (or any child) matches the current path.
  const [expandedFor, setExpandedFor] = useState<{
    pathname: string;
    items: typeof availableItems;
  } | null>(null);
  if (expandedFor?.pathname !== location.pathname || expandedFor.items !== availableItems) {
    setExpandedFor({ pathname: location.pathname, items: availableItems });
    setOpenGroups((prev) => {
      let next = prev;
      for (const item of availableItems) {
        if (!item.children?.length) continue;
        const childMatch = item.children.some((child) =>
          pathMatchesNavItem(location.pathname, child)
        );
        if (childMatch || pathMatchesNavItem(location.pathname, item)) {
          if (!prev[item.id]) {
            if (next === prev) next = { ...prev };
            next[item.id] = true;
          }
        }
      }
      return next;
    });
  }

  // While searching, keep groups with visible children expanded.
  const isFiltering = searchQuery.trim().length > 0;

  // A new search starts with nothing highlighted; fewer results keep the highlight in range.
  const [highlightFor, setHighlightFor] = useState({ searchQuery, count: navTargets.length });
  if (highlightFor.searchQuery !== searchQuery || highlightFor.count !== navTargets.length) {
    setHighlightFor({ searchQuery, count: navTargets.length });
    if (highlightFor.searchQuery !== searchQuery) {
      setHighlightedIndex(-1);
    } else {
      setHighlightedIndex((index) => {
        if (index < 0) return index;
        if (navTargets.length === 0) return -1;
        return Math.min(index, navTargets.length - 1);
      });
    }
  }

  const activeId = getActiveNavItemId(location.pathname, availableItems);

  const closeMobile = () => {
    if (isMobile) setOpenMobile(false);
  };

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const filtering = searchQuery.trim().length > 0;

    if (e.key === 'Escape') {
      if (!filtering) return;
      e.preventDefault();
      setSearchQuery('');
      setHighlightedIndex(-1);
      // Clear only — do not blur or collapse the sidebar
      return;
    }

    if (!filtering || navTargets.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((index) =>
        index < 0 ? 0 : (index + 1) % navTargets.length
      );
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((index) =>
        index <= 0 ? navTargets.length - 1 : index - 1
      );
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      const index = highlightedIndex >= 0 ? highlightedIndex : 0;
      const target = navTargets[index];
      if (!target) return;
      const el = document.querySelector<HTMLElement>(
        `[data-sidebar-item-key="${target.id}"]`
      );
      setSearchQuery('');
      setHighlightedIndex(-1);
      closeMobile();
      if (el) {
        el.click();
      } else {
        navigate(target.path);
      }
    }
  };

  const listboxId = 'nav-search-results';
  const highlightedId =
    highlightedIndex >= 0 && navTargets[highlightedIndex]
      ? navTargets[highlightedIndex].id
      : null;
  const activeDescendantId = highlightedId
    ? `nav-result-${highlightedId}`
    : undefined;

  const clearSearchAndClose = () => {
    setSearchQuery('');
    setHighlightedIndex(-1);
    closeMobile();
  };

  const environment = environmentLabel(currentEnvironment());

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="gap-2">
        <div className="hidden items-center gap-2 px-2 pt-1 md:flex group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
          <Link
            to="/"
            className="flex min-w-0 flex-1 items-center gap-1.5 whitespace-nowrap text-[13px] font-semibold tracking-tight text-sidebar-foreground group-data-[collapsible=icon]:hidden"
          >
            <img src="/favicon.svg" alt="" className="size-5 shrink-0 rounded" />
            {PRODUCT_NAME}
          </Link>
          <SidebarTrigger className="ml-auto group-data-[collapsible=icon]:ml-0" />
        </div>
        {environment ? (
          <div
            className="mx-2 rounded-sm border border-warning-border bg-warning-soft px-2 py-0.5 text-center text-3xs font-bold tracking-widest text-warning-strong group-data-[collapsible=icon]:hidden"
            data-hint={`Not production: this copy runs on ${window.location.host}`}
          >
            {environment}
          </div>
        ) : null}
        <div className="px-2 group-data-[collapsible=icon]:hidden">
          <SearchInput
              size="sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClear={() => {
                setSearchQuery('');
                setHighlightedIndex(-1);
              }}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search navigation…"
              aria-label="Search navigation"
              role="combobox"
              aria-expanded={navTargets.length > 0}
              aria-controls={listboxId}
              aria-activedescendant={activeDescendantId}
              aria-autocomplete="list"
            />
        </div>
      </SidebarHeader>

      <SidebarContent>
        {visibleItems.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-muted-foreground group-data-[collapsible=icon]:hidden">
            <p>No matching pages.</p>
            <Button
              type="button"
              variant="link"
              className="mt-1 h-auto p-0"
              onClick={() => {
                setSearchQuery('');
                setHighlightedIndex(-1);
              }}
            >
              Clear search
            </Button>
          </div>
        ) : (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu id={listboxId} role="listbox" aria-label="Navigation results">
                {visibleItems.map((item) => {
                  if (item.children?.length) {
                    const open = isFiltering || Boolean(openGroups[item.id]);
                    return (
                      <NavGroupItem
                        key={item.id}
                        item={item}
                        activeId={activeId}
                        highlightedId={highlightedId}
                        open={open}
                        onOpenChange={(next) =>
                          setOpenGroups((prev) => ({ ...prev, [item.id]: next }))
                        }
                        onNavigate={clearSearchAndClose}
                        onParentNavigate={() => {
                          setOpenGroups((prev) => ({ ...prev, [item.id]: true }));
                          clearSearchAndClose();
                        }}
                      />
                    );
                  }
                  return (
                    <NavLinkItem
                      key={item.id}
                      item={item}
                      isActive={activeId === item.id}
                      isHighlighted={highlightedId === item.id}
                      onNavigate={clearSearchAndClose}
                    />
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>
      <SidebarFooter>
        <SidebarTasksButton />
      </SidebarFooter>
    </Sidebar>
  );
}
